import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import ArrowForward from "@mui/icons-material/ArrowForward";
import ArrowOutward from "@mui/icons-material/ArrowOutward";
import Download from "@mui/icons-material/Download";
import MissionPreview from "../components/MissionPreview";
import MomentumField from "../components/motion/MomentumField";
import WorkforceFormation from "../components/motion/WorkforceFormation";
import WorkerScale from "../components/motion/WorkerScale";
import Autopilot from "../components/motion/Autopilot";
import { FacetGlyph } from "../components/motion/Glyphs";
import {
  DownloadRoute,
  KineticTitle,
  MissionRoute,
  Scribble,
} from "../components/motion/Flourishes";
import { useInView } from "../components/motion/motion";
import { PAGE_META } from "../utils/shareMeta";
import {
  SiteShell,
  BrandLink,
  SectionHead,
  NextChapter,
  ProductPaths,
  DEMO_URL,
  RELEASE_URL,
} from "../components/BrandPrimitives";
const facets = [
  [
    "A clear mission.",
    "Start with the outcome. Grow product adoption, launch a campaign, hire your team, or keep company operations moving. Your worker takes it from there.",
  ],
  [
    "Work in motion.",
    "Momentum carries work across devices and apps, using the tools and sessions you authorize. Follow the mission as it happens.",
  ],
  [
    "You stay in control.",
    "Set the direction and boundaries. Momentum carries the work forward and brings you in when your judgment is needed.",
  ],
];
const ROUTE_STOPS = ["#momentum", ".brand-platform-band", ".brand-belief", "#downloads"];
const pinTop = (track) => parseFloat(getComputedStyle(track.firstElementChild).top) || 0;
const pinStart = (track) => track.getBoundingClientRect().top + window.scrollY - pinTop(track);
const pinEnd = (track) => pinStart(track) + track.offsetHeight - track.firstElementChild.offsetHeight;
const AUTOPILOT_STOPS = [
  { selector: ".brand-hero-product", label: "Momentum at work", align: 0.1, dwell: 3400 },
  { selector: "#momentum", label: "Set the destination. Watch the work happen.", align: 0.12 },
  { selector: ".brand-feature-tabs", label: "A clear mission. Work in motion. You stay in control.", align: 0.28 },
  { selector: ".bm-scale-track", label: "Start with one digital humanoid worker.", to: pinStart, dwell: 2200 },
  { selector: ".bm-scale-track", label: "Spin up a team, a company, a workforce.", to: pinEnd, dwell: 3600, duration: 7000 },
  { selector: ".bm-formation-track", label: "Intelligence…", to: pinStart, dwell: 1600 },
  { selector: ".bm-formation-track", label: "…becomes a workforce. You lead.", to: pinEnd, dwell: 3200, duration: 5600 },
  { selector: "#downloads", label: "Destination: Meet your Momentum.", align: 0.22, dwell: 2400 },
];
export default function BrandHomePage() {
  const [facet, setFacet] = useState(0);
  const [autoFacet, setAutoFacet] = useState(true);
  const hero = useRef(null);
  const tabs = useRef(null);
  const downloadBand = useRef(null);
  const tabsInView = useInView(tabs);
  const [platform, setPlatform] = useState(() =>
    /win/i.test((typeof navigator === "undefined" ? "" : navigator.userAgent))
      ? "windows"
      : /linux/i.test((typeof navigator === "undefined" ? "" : navigator.userAgent))
        ? "linux"
        : "mac_arm",
  );
  const downloads = {
    mac_arm: ["macOS / Apple Silicon", "MeetingMachine-arm64.dmg"],
    mac_intel: ["macOS / Intel", "MeetingMachine-x64.dmg"],
    windows: ["Windows", "The.Meeting.Machine_x64-setup.exe"],
    linux: ["Linux", "the-meeting-machine_amd64.AppImage"],
  };
  return (
    <SiteShell {...PAGE_META.home}>
      <MissionRoute stops={ROUTE_STOPS} />
      <section className="brand-hero" aria-labelledby="home-title" ref={hero}>
        <MomentumField hostRef={hero} />
        <div className="brand-container brand-hero-content">
          <span className="brand-eyebrow">
            GrupaAI / Your digital humanoid worker
          </span>
          <KineticTitle id="home-title" text="Momentum" />
          <p className="brand-hero-statement">
            The AI to run your business
            <br />
            <span>on autopilot and grow it.</span>
          </p>
          <p className="brand-hero-description">
            A digital humanoid worker across your devices and in the cloud.
            <br />
            Set the mission. Get the outcome.
          </p>
          <div className="brand-actions">
            <BrandLink to="/desktop#downloads">Get Momentum</BrandLink>
            <a
              className="brand-text-link"
              href={DEMO_URL}
              target="_blank"
              rel="noreferrer"
            >
              Let&apos;s talk <ArrowOutward fontSize="small" />
            </a>
          </div>
          <Autopilot stops={AUTOPILOT_STOPS} />
        </div>
      </section>
      <div className="brand-container brand-hero-product">
        <MissionPreview />
      </div>
      <div className="brand-customers brand-container">
        <span className="brand-caption">In the company of ambitious teams</span>
        <div>
          {[
            ["Rivet", "rivet.svg", "https://rivet.design/"],
            ["Rho", "rho.svg", "https://www.rho.co/"],
            ["Surtona", "surtona.png", "https://www.surtona.com/"],
            ["Mica", "mica.svg", "https://www.usemica.com/"],
            [
              "New York Life",
              "newyorklife.svg",
              "https://www.newyorklife.com/",
            ],
          ].map(([name, file, href]) => (
            <a key={name} href={href} target="_blank" rel="noreferrer">
              <img src={"/customer-logos/" + file} alt={name} loading="lazy" />
            </a>
          ))}
        </div>
      </div>
      <section id="momentum" className="brand-section brand-container">
        <SectionHead
          eyebrow="01 / Meet Momentum"
          title={
            <>
              Set the destination.
              <br />
              Watch the work happen.
            </>
          }
        >
          Momentum works across devices and apps to carry out the mission,
          coordinating screens in parallel, locally or remotely. Follow the work
          from your desktop or phone.
        </SectionHead>
        <div className="brand-mission-benefits">
          <span className="brand-eyebrow">
            Your devices. Your identity. Your outcome.
          </span>
          <p className="bm-marker">
            <span style={{ "--m": 0 }}>Customers won.</span>{" "}
            <span style={{ "--m": 1 }}>Revenue grown.</span>{" "}
            <span style={{ "--m": 2 }}>Great people hired.</span>
          </p>
          <span className="brand-caption">
            From acquiring customers and hiring talent to launching products,
            supporting customers, and running daily operations.
          </span>
        </div>
        <div
          ref={tabs}
          className={"brand-feature-tabs" + (tabsInView ? "" : " is-paused")}
        >
          {facets.map(([title, text], i) => (
            <button
              key={title}
              aria-pressed={facet === i}
              onClick={() => {
                setFacet(i);
                setAutoFacet(false);
              }}
              onAnimationEnd={(e) => {
                if (autoFacet && e.animationName === "bm-facet-progress")
                  setFacet((i + 1) % facets.length);
              }}
              className={
                (facet === i ? "is-selected" : "") + (autoFacet ? " is-auto" : "")
              }
            >
              <span className="brand-caption">0{i + 1}</span>
              <FacetGlyph index={i} />
              <h3>{title}</h3>
              <p>{text}</p>
            </button>
          ))}
        </div>
        <div
          className="brand-facet-detail"
          aria-live={autoFacet ? "off" : "polite"}
        >
          <span className="brand-status-dot" />
          <span key={facet}>
            {
              [
                "Define the outcome. Start the mission.",
                "Follow execution across your everyday tools.",
                "Your direction. Your boundaries. Work that keeps moving.",
              ][facet]
            }
          </span>
          <Link to="/workspace">
            Explore Workspace <ArrowForward fontSize="small" />
          </Link>
        </div>
      </section>
      <section className="brand-platform-band">
        <div className="brand-container brand-section">
          <SectionHead
            eyebrow="02 / Built to grow with you"
            title={
              <>
                Start with one.
                <br />
                Think without limits.
              </>
            }
          >
            Run your business with Momentum and Workspace. Build your own
            AI-native products and companies on Workers API. One connected
            foundation.
          </SectionHead>
          <WorkerScale />
          <ProductPaths />
        </div>
      </section>
      <section className="brand-belief brand-container">
        <div>
          <span className="brand-eyebrow">03 / The Pioneer AI Lab</span>
          <h2>
            Intelligence becomes
            <br />a workforce.
            <br />
            <em className="bm-scribble-host">
              You lead.
              <Scribble />
            </em>
          </h2>
        </div>
        <div className="brand-belief-copy">
          <p>
            One worker. A team.
            <br />
            An autonomous organization.
          </p>
          <p>
            GrupaAI builds the operating system for autonomous work. Digital
            humanoid workers carry out the execution, so people can focus on
            vision, relationships, and leading the business.
          </p>
          <Link to="/vision" className="brand-text-link">
            Read our vision <ArrowOutward fontSize="small" />
          </Link>
        </div>
        <WorkforceFormation />
      </section>
      <section id="downloads" className="brand-download-band" ref={downloadBand}>
        <DownloadRoute hostRef={downloadBand} />
        <div className="brand-container brand-download-inner">
          <div>
            <span className="brand-eyebrow">Make your next move</span>
            <h2>Meet your Momentum.</h2>
            <p>Your next chapter starts with one worker.</p>
          </div>
          <div className="brand-download-control">
            <label htmlFor="momentum-platform">Choose your platform</label>
            <select
              id="momentum-platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
            >
              {Object.entries(downloads).map(([key, [label]]) => (
                <option value={key} key={key}>
                  {label}
                </option>
              ))}
            </select>
            <a
              className="brand-button"
              href={RELEASE_URL + "/" + downloads[platform][1]}
            >
              <Download fontSize="small" /> Download Momentum
            </a>
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noreferrer"
              className="brand-text-link"
            >
              See it with our team <ArrowOutward fontSize="small" />
            </a>
          </div>
        </div>
      </section>
      <NextChapter
        eyebrow="Built by people who care"
        title="The future needs pioneers."
        text="Help us make expert digital humanoid workers accessible to everyone."
        to="/careers"
        label="Find your place"
      />
    </SiteShell>
  );
}
